import { Text, useTexture } from "@react-three/drei";
import { useFrame, useThree } from "@react-three/fiber";
import { useEffect, useMemo, useRef, type ReactNode } from "react";
import {
  Group,
  LinearFilter,
  LinearMipmapLinearFilter,
  MathUtils,
  Mesh,
  SRGBColorSpace,
  Texture
} from "three";
import { aboutTeam, teamSceneImageFallback } from "../../content/siteContent";
import { sectionEntryConfigs } from "../../lib/sectionEntry";
import { getViewportTier, isCompactViewport, isMobileViewport } from "../../lib/viewport";
import { useSectionEntryStore } from "../../store/useSectionEntryStore";
import { getTeamActiveMemberIndex, getTeamMemberWeights } from "./teamScenePhase";

type TeamSceneProps = {
  weight: number;
  reducedMotion: boolean;
};

type Vec3Tuple = [number, number, number];

type TeamHero3DTextProps = {
  children: ReactNode;
  position?: Vec3Tuple;
  rotation?: Vec3Tuple;
  fontSize: number;
  opacity: number;
  anchorX?: "left" | "center" | "right";
  anchorY?: "top" | "middle" | "bottom";
  maxWidth?: number;
  lineHeight?: number;
  letterSpacing?: number;
  color?: string;
};

type TeamSceneLayout = {
  basePosition: Vec3Tuple;
  baseScale: number;
  portfolioTextPosition: Vec3Tuple;
  portfolioTextFontSize: number;
  portfolioTextRotation: Vec3Tuple;
  quotePosition: Vec3Tuple;
  quoteFontSize: number;
  quoteMaxWidth: number;
  rolePosition: Vec3Tuple;
  roleFont: number;
  roleMaxWidth: number;
  namePosition: Vec3Tuple;
  nameFont: number;
  counterPosition: Vec3Tuple;
  bodyPosition: Vec3Tuple;
  bodyFont: number;
  bodyMaxWidth: number;
  portraitPosition: Vec3Tuple;
  portraitLandingStartY: number;
  portraitLandingEndY: number;
  portraitLandingStartZ: number;
  portraitLandingEndZ: number;
  portraitScale: Vec3Tuple;
  floorPosition: Vec3Tuple;
  floorScale: Vec3Tuple;
};

function formatRole(role: string) {
  return role
    .split(" ")
    .filter(Boolean)
    .map((part) => part.toUpperCase())
    .join("\n");
}

function formatCounter(index: number, total: number) {
  return `${String(index + 1).padStart(2, "0")} / ${String(total).padStart(2, "0")}`;
}

function setTextureQuality(texture: Texture) {
  texture.colorSpace = SRGBColorSpace;
  texture.minFilter = LinearMipmapLinearFilter;
  texture.magFilter = LinearFilter;
  texture.generateMipmaps = true;
  texture.needsUpdate = true;
}

function TeamHero3DText({
  anchorX = "center",
  anchorY = "middle",
  children,
  color = "#17151b",
  fontSize,
  letterSpacing = 0,
  lineHeight = 1,
  maxWidth,
  opacity,
  position = [0, 0, 0],
  rotation = [0, 0, 0]
}: TeamHero3DTextProps) {
  // This wrapper keeps the large text layer swappable with drei Text3D when a typeface JSON is added.
  return (
    <Text
      anchorX={anchorX}
      anchorY={anchorY}
      color={color}
      fillOpacity={opacity}
      fontSize={fontSize}
      letterSpacing={letterSpacing}
      lineHeight={lineHeight}
      maxWidth={maxWidth}
      position={position}
      rotation={rotation}
    >
      {children}
    </Text>
  );
}

export function TeamScene({ reducedMotion, weight }: TeamSceneProps) {
  const viewport = useThree((state) => getViewportTier(state.size.width));
  const teamEntryState = useSectionEntryStore((state) => state.sections.team);
  const groupRef = useRef<Group>(null);
  const portraitRef = useRef<Group>(null);
  const portfolioRef = useRef<Group>(null);
  const roleRef = useRef<Group>(null);
  const floorRef = useRef<Mesh>(null);
  const textures = useTexture(
    useMemo(() => aboutTeam.map((member) => member.imageSrc ?? teamSceneImageFallback), [])
  ) as Texture[];
  const visible = weight > 0.001;
  const opacity = Math.min(weight, 1);
  const compact = isCompactViewport(viewport);
  const mobile = isMobileViewport(viewport);
  const layout: TeamSceneLayout = mobile
    ? {
      basePosition: [0, 0.02, -2.42],
      baseScale: 0.92,
      portfolioTextPosition: [0, 1.42, -0.82],
      portfolioTextFontSize: 0.46,
      portfolioTextRotation: [0, 0, 0],
      quotePosition: [0, 1.08, -0.18],
      quoteFontSize: 0.046,
      quoteMaxWidth: 1.35,
      rolePosition: [-0.72, 0.58, 0.04],
      roleFont: 0.145,
      roleMaxWidth: 1.15,
      namePosition: [-0.72, 0.77, 0.02],
      nameFont: 0.06,
      counterPosition: [-0.72, -0.84, 0.04],
      bodyPosition: [-0.72, -0.98, 0.04],
      bodyFont: 0.1,
      bodyMaxWidth: 1.4,
      portraitPosition: [0.18, 0.02, 0.18],
      portraitLandingStartY: 1.35,
      portraitLandingEndY: 0.02,
      portraitLandingStartZ: 0.48,
      portraitLandingEndZ: 0.18,
      portraitScale: [1.42, 1.62, 1],
      floorPosition: [0.12, -1.0, -0.28],
      floorScale: [1.38, 0.3, 1]
    }
    : compact
      ? {
        basePosition: [0.02, 0.04, -2.16],
        baseScale: 0.92,
        portfolioTextPosition: [0.06, 1.12, -0.78],
        portfolioTextFontSize: 0.92,
        portfolioTextRotation: [0, 0.02, 0],
        quotePosition: [-0.78, 0.52, -0.28],
        quoteFontSize: 0.078,
        quoteMaxWidth: 2.2,
        rolePosition: [-2.12, 0.24, 0.02],
        roleFont: 0.3,
        roleMaxWidth: 1.95,
        namePosition: [-2.12, 0.58, 0.03],
        nameFont: 0.1,
        counterPosition: [1.45, 0.48, 0.02],
        bodyPosition: [1.45, 0.18, 0.02],
        bodyFont: 0.1,
        bodyMaxWidth: 1.75,
        portraitPosition: [0.34, -0.26, 0.18],
        portraitLandingStartY: 1.9,
        portraitLandingEndY: -0.26,
        portraitLandingStartZ: 0.6,
        portraitLandingEndZ: 0.18,
        portraitScale: [2.05, 1.52, 1],
        floorPosition: [0.32, -1.4, -0.28],
        floorScale: [2.55, 0.45, 1]
      }
      : {
        basePosition: [0, -0.02, -2.24],
        baseScale: 1,
        portfolioTextPosition: [0.12, 1.45, -0.82],
        portfolioTextFontSize: 1.55,
        portfolioTextRotation: [0, 0.025, 0],
        quotePosition: [-0.85, 0.72, -0.18],
        quoteFontSize: 0.085,
        quoteMaxWidth: 2.8,
        rolePosition: [-3.05, 0.18, 0.02],
        roleFont: 0.42,
        roleMaxWidth: 2.35,
        namePosition: [-3.05, 0.5, 0.03],
        nameFont: 0.12,
        counterPosition: [2.15, 0.44, 0.02],
        bodyPosition: [2.15, 0.12, 0.02],
        bodyFont: 0.115,
        bodyMaxWidth: 2.1,
        portraitPosition: [0.8, -0.32, 0.22],
        portraitLandingStartY: 2.0,
        portraitLandingEndY: -0.32,
        portraitLandingStartZ: 0.64,
        portraitLandingEndZ: 0.22,
        portraitScale: [3.15, 1.85, 1],
        floorPosition: [0.7, -1.42, -0.28],
        floorScale: [3.5, 0.46, 1]
      };
  const baseScale = layout.baseScale;
  const basePosition = layout.basePosition;
  const landingProgress = reducedMotion ? 1 : teamEntryState.entryProgress;
  const browseProgress = teamEntryState.browseProgress;
  const initialMemberIndex = sectionEntryConfigs.team?.initialStateIndex ?? 0;
  const memberWeights = getTeamMemberWeights(browseProgress, aboutTeam.length, initialMemberIndex);
  const activeMemberIndex = getTeamActiveMemberIndex(browseProgress, aboutTeam.length, initialMemberIndex);

  useEffect(() => {
    textures.forEach(setTextureQuality);
  }, [textures]);

  useFrame((state, delta) => {
    if (!groupRef.current || !portraitRef.current || !portfolioRef.current || !roleRef.current || !floorRef.current) {
      return;
    }

    const elapsed = state.clock.elapsedTime;
    const idle = reducedMotion ? 0 : 1;
    const pointerX = reducedMotion ? 0 : state.pointer.x;
    const pointerY = reducedMotion ? 0 : state.pointer.y;

    const targetX = basePosition[0] + pointerX * (mobile ? 0.06 : 0.1) + Math.sin(elapsed * 0.16) * 0.03 * idle;
    const targetY = basePosition[1] + pointerY * 0.06 + Math.cos(elapsed * 0.2) * 0.03 * idle;
    const targetZ = basePosition[2] + Math.sin(teamEntryState.rawProgress * Math.PI) * -0.08;

    groupRef.current.position.x = MathUtils.damp(groupRef.current.position.x, targetX, 4.2, delta);
    groupRef.current.position.y = MathUtils.damp(groupRef.current.position.y, targetY, 4.2, delta);
    groupRef.current.position.z = MathUtils.damp(groupRef.current.position.z, targetZ, 4.2, delta);
    groupRef.current.rotation.y = MathUtils.damp(
      groupRef.current.rotation.y,
      pointerX * -0.06 + (teamEntryState.rawProgress - 0.5) * 0.08,
      4.2,
      delta
    );
    groupRef.current.rotation.x = MathUtils.damp(groupRef.current.rotation.x, pointerY * 0.025, 4.2, delta);
    const targetScaleX = mobile ? baseScale * 0.88 : baseScale;
    groupRef.current.scale.x = MathUtils.damp(groupRef.current.scale.x, targetScaleX, 4.2, delta);
    groupRef.current.scale.y = MathUtils.damp(groupRef.current.scale.y, baseScale, 4.2, delta);
    groupRef.current.scale.z = MathUtils.damp(groupRef.current.scale.z, baseScale, 4.2, delta);

    portfolioRef.current.position.x = MathUtils.damp(
      portfolioRef.current.position.x,
      layout.portfolioTextPosition[0] + pointerX * (mobile ? -0.035 : -0.075),
      4.6,
      delta
    );
    portfolioRef.current.position.y = MathUtils.damp(
      portfolioRef.current.position.y,
      layout.portfolioTextPosition[1] + pointerY * (mobile ? 0.018 : 0.035),
      4.6,
      delta
    );
    portfolioRef.current.position.z = MathUtils.damp(portfolioRef.current.position.z, layout.portfolioTextPosition[2], 4.6, delta);
    portfolioRef.current.rotation.y = MathUtils.damp(
      portfolioRef.current.rotation.y,
      layout.portfolioTextRotation[1] + pointerX * (mobile ? 0.015 : 0.035),
      4.6,
      delta
    );

    roleRef.current.rotation.y = MathUtils.damp(roleRef.current.rotation.y, pointerX * -0.035, 4.8, delta);
    roleRef.current.rotation.x = MathUtils.damp(roleRef.current.rotation.x, pointerY * 0.012, 4.8, delta);

    const landingY = MathUtils.lerp(layout.portraitLandingStartY, layout.portraitLandingEndY, landingProgress);
    const landingZ = MathUtils.lerp(layout.portraitLandingStartZ, layout.portraitLandingEndZ, landingProgress);
    const driftY = Math.sin(elapsed * 0.5) * 0.03 * idle;
    const portraitFinalRotationX = mobile ? -0.01 : -0.02;
    const portraitBaseRotationY = mobile ? 0.025 : 0.05;
    const portraitFinalRotationZ = mobile ? -0.006 : -0.015;

    portraitRef.current.position.x = MathUtils.damp(portraitRef.current.position.x, layout.portraitPosition[0], 4.4, delta);
    portraitRef.current.position.y = MathUtils.damp(portraitRef.current.position.y, landingY + driftY, 4.4, delta);
    portraitRef.current.position.z = MathUtils.damp(portraitRef.current.position.z, landingZ, 4.4, delta);
    portraitRef.current.rotation.x = MathUtils.damp(
      portraitRef.current.rotation.x,
      MathUtils.lerp(-0.22, portraitFinalRotationX, landingProgress),
      4.4,
      delta
    );
    portraitRef.current.rotation.y = MathUtils.damp(
      portraitRef.current.rotation.y,
      portraitBaseRotationY + browseProgress * (mobile ? 0.018 : 0.035) - pointerX * (mobile ? 0.018 : 0.035),
      4.4,
      delta
    );
    portraitRef.current.rotation.z = MathUtils.damp(
      portraitRef.current.rotation.z,
      MathUtils.lerp(0.06, portraitFinalRotationZ, landingProgress) - pointerY * (mobile ? 0.006 : 0.012),
      4.4,
      delta
    );
    portraitRef.current.scale.x = MathUtils.damp(portraitRef.current.scale.x, layout.portraitScale[0], 4.4, delta);
    portraitRef.current.scale.y = MathUtils.damp(portraitRef.current.scale.y, layout.portraitScale[1], 4.4, delta);
    portraitRef.current.scale.z = MathUtils.damp(portraitRef.current.scale.z, layout.portraitScale[2], 4.4, delta);

    floorRef.current.scale.x = MathUtils.damp(floorRef.current.scale.x, layout.floorScale[0], 4.2, delta);
    floorRef.current.scale.y = MathUtils.damp(
      floorRef.current.scale.y,
      MathUtils.lerp(0.22, layout.floorScale[1], landingProgress),
      4.2,
      delta
    );
  });

  return (
    <group ref={groupRef} position={basePosition} scale={baseScale} visible={visible}>
      <group ref={portfolioRef} position={layout.portfolioTextPosition} rotation={layout.portfolioTextRotation}>
        <TeamHero3DText
          color="#050505"
          fontSize={layout.portfolioTextFontSize}
          letterSpacing={0.02}
          opacity={(mobile ? 0.1 : 0.18) * opacity}
          position={[0.05, -0.06, -0.12]}
        >
          PORTFOLIO
        </TeamHero3DText>
        <TeamHero3DText
          fontSize={layout.portfolioTextFontSize}
          letterSpacing={0.02}
          opacity={(mobile ? 0.38 : compact ? 0.5 : 0.58) * opacity}
          position={[0, 0, 0]}
        >
          PORTFOLIO
        </TeamHero3DText>
      </group>



      {aboutTeam.map((member, index) => {
        const memberOpacity = memberWeights[index] * opacity;
        if (memberOpacity <= 0.001) {
          return null;
        }

        return (
          <group key={member.name} position={[0, 0, -index * 0.002]}>
            <group ref={index === activeMemberIndex ? roleRef : undefined} position={layout.rolePosition}>
              <TeamHero3DText
                anchorX="left"
                anchorY="top"
                color="#080708"
                fontSize={layout.roleFont}
                lineHeight={0.84}
                maxWidth={layout.roleMaxWidth}
                opacity={0.18 * memberOpacity}
                position={[0.025, -0.03, -0.045]}
              >
                {formatRole(member.role)}
              </TeamHero3DText>
              <TeamHero3DText
                anchorX="left"
                anchorY="top"
                color="#17151b"
                fontSize={layout.roleFont}
                lineHeight={0.84}
                maxWidth={layout.roleMaxWidth}
                opacity={0.96 * memberOpacity}
                position={[0, 0, 0]}
              >
                {formatRole(member.role)}
              </TeamHero3DText>
            </group>

            <group position={[layout.namePosition[0], layout.namePosition[1], layout.namePosition[2]]}>
              <Text
                anchorX="left"
                anchorY="middle"
                color="#17151b"
                fillOpacity={0.58 * memberOpacity}
                fontSize={layout.nameFont}
                letterSpacing={0.08}
                position={[0, 0, 0]}
              >
                {member.name.toUpperCase()}
              </Text>
            </group>

            <group position={layout.counterPosition}>
              <Text
                anchorX="left"
                anchorY="middle"
                color="#17151b"
                fillOpacity={0.54 * memberOpacity}
                fontSize={mobile ? 0.068 : 0.09}
                letterSpacing={0.12}
                position={[0, 0, 0]}
              >
                {formatCounter(activeMemberIndex, aboutTeam.length)}
              </Text>
            </group>

            <group position={layout.bodyPosition}>
              <Text
                anchorX="left"
                anchorY="top"
                color="#2f2a28"
                fillOpacity={0.74 * memberOpacity}
                fontSize={layout.bodyFont}
                lineHeight={1.55}
                maxWidth={layout.bodyMaxWidth}
                position={[0, 0, 0]}
              >
                {member.body}
              </Text>
            </group>
          </group>
        );
      })}

      <group
        ref={portraitRef}
        position={[layout.portraitPosition[0], layout.portraitLandingStartY, layout.portraitLandingStartZ]}
        rotation={[-0.22, 0.05, 0.06]}
        scale={layout.portraitScale}
      >
        {!mobile ? (
          <mesh position={[0, -0.08, -0.045]} scale={[1.04, 1.06, 1]}>
            <boxGeometry args={[1, 1, 0.045]} />
            <meshStandardMaterial color="#fff8f000" transparent opacity={0 * opacity} />
          </mesh>
        ) : null}

        {textures.map((texture, index) => {
          const memberOpacity = memberWeights[index] * opacity;
          if (memberOpacity <= 0.001) {
            return null;
          }

          return (
            <mesh
              key={`${aboutTeam[index]?.name ?? index}-portrait`}
              position={[0, 0, index * 0.001]}
            >
              <boxGeometry args={[1, 1, 0.045]} />
              <meshStandardMaterial map={texture} toneMapped={false} transparent opacity={0.98 * memberOpacity} />
            </mesh>
          );
        })}
      </group>

      <mesh
        ref={floorRef}
        position={layout.floorPosition}
        rotation={[-Math.PI / 2, 0, 0]}
        scale={layout.floorScale}
      >
        <circleGeometry args={[1, 64]} />
        <meshBasicMaterial color="#211d1a" depthWrite={false} transparent opacity={0.08 * opacity} />
      </mesh>
    </group>
  );
}
